import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../api/axiosConfig';
import { usePublicLanguage } from '../contexts/PublicLanguageContext';

// Seamless sign-in for users arriving from the mobile app. The app opens
// /handoff?code=…&next=pricing with a short-lived signed code; we redeem it
// for the auth token, store it exactly like a normal login, and land the
// user where they were headed — no retyping credentials on a phone browser.
const ALLOWED_NEXT: Record<string, string> = {
  pricing: '/pricing',
};

const Handoff: React.FC = () => {
  const { t } = usePublicLanguage();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = params.get('code');
    const dest = ALLOWED_NEXT[params.get('next') || ''] || '/';
    if (!code) {
      setError(t.auth.handoffMissingCode);
      return;
    }
    (async () => {
      try {
        const { data } = await api.post('/auth/web-handoff/redeem/', { code });
        localStorage.setItem('token', data.token);
        // Full navigation so App re-reads auth state from localStorage.
        window.location.assign(dest);
      } catch (e: any) {
        setError(
          e?.response?.data?.detail ||
          t.auth.handoffInvalid
        );
      }
    })();
  }, [params, t]);

  return (
    <div style={{
      minHeight: '60vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24,
      textAlign: 'center',
    }}>
      {error ? (
        <>
          <h2>{t.auth.handoffFailedTitle}</h2>
          <p style={{ maxWidth: 420, color: '#666' }}>{error}</p>
          <Link to="/login" className="cta-button primary">{t.auth.handoffLogIn}</Link>
        </>
      ) : (
        <>
          <div className="handoff-spinner" style={{
            width: 36, height: 36, borderRadius: '50%',
            border: '4px solid #ccc', borderTopColor: '#2aa1b5',
            animation: 'handoff-spin 0.8s linear infinite',
          }} />
          <p style={{ color: '#666' }}>{t.auth.handoffSigningIn}</p>
          <style>{'@keyframes handoff-spin{to{transform:rotate(360deg)}}'}</style>
        </>
      )}
    </div>
  );
};

export default Handoff;
